import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { isNotFoundRoute, isOpenToVisitors, isPortalPath } from '../router/public-routes.js'
import { useAuth } from './auth/useAuth.js'

/**
 * « Publique » veut dire ici : PAS D'HABILLAGE DE BACK-OFFICE (ni barre
 * latérale, ni en-tête). C'est une question voisine de celle de
 * `router/public-routes.js` — « atteignable sans session » — mais pas la même :
 * l'écran de connexion et les trois espaces de portail exigent une session (ou
 * la demandent), et n'ont pourtant rien à faire de la navigation du back-office.
 *
 * Ce fichier tenait donc SA PROPRE liste, forcément divergente : c'est ainsi
 * que `/inscription-formateur/verifier-email` était publique ici et privée
 * là-bas, et qu'un 401 y a expédié des formateurs vers l'écran de connexion du
 * back-office. Il n'y a plus qu'une liste, celle de `router/public-routes.js`,
 * et ce composable n'y ajoute que ce qui lui est propre.
 */
const SANS_HABILLAGE = ['/login']

export function useAppRouting() {
  const route = useRoute()
  const router = useRouter()
  const { user } = useAuth()

  // Avant que le routeur n'ait résolu la navigation initiale, route.path vaut '/'
  // (START_LOCATION) et serait considéré comme une route privée → la sidebar
  // flasherait sur les pages publiques. On attend donc que le routeur soit prêt
  // avant d'autoriser l'affichage du header.
  const isRouterReady = ref(false)
  router.isReady().then(() => { isRouterReady.value = true })

  const isEmbedRoute = computed(() => route.path.startsWith('/embed'))

  const isPublicRoute = computed(() =>
    SANS_HABILLAGE.includes(route.path)
    || isOpenToVisitors(route.path)
    // Le préfixe seul ou suivi d'une barre (`isPortalPath`), jamais un
    // `startsWith` nu : `/espace-formateurXYZ` n'est pas une page du portail
    // formateur, c'est une adresse inconnue.
    || isPortalPath(route.path)
    // Page introuvable sans session du back-office : écran public. La garde a
    // déjà interrogé la session avant que la route ne soit résolue, donc
    // `user` est connu quand ce nom apparaît. Connecté, on garde sa barre.
    || (isNotFoundRoute(route) && !user.value)
  )

  const showHeader = computed(() => isRouterReady.value && !isPublicRoute.value)

  return {
    isEmbedRoute,
    isPublicRoute,
    // « La route est-elle connue ? ». Tant qu'elle ne l'est pas, `route.path`
    // vaut « / » (START_LOCATION) et `isPublicRoute` répond FAUX pour tout le
    // monde — y compris pour une page de portail. Ce qui s'appuie dessus pour
    // décider de faire un appel AUTHENTIFIÉ doit donc attendre ce drapeau,
    // sinon l'appel part quand même et son 401 se lit comme une session
    // expirée (cf. les gardes de portail, qui sont asynchrones : la fenêtre
    // dure le temps d'un aller-retour serveur, largement de quoi monter des
    // composants).
    isRouterReady,
    showHeader
  }
}
